import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';
import { fetchFormData, createExpertReport, patchAccidentFileInsuranceApplication } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const InsuranceCompanyReportForm = ({ formData, errors, setFormData, workFileTypes, fieldPermissions, canEdit = true }) => {
    const { id } = useParams(); // Get call form ID
    const [reportTemplates, setReportTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedFileNumber, setSelectedFileNumber] = useState('');
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);
    
    // State for insurance application info for each accident file
    const [insuranceApplicationData, setInsuranceApplicationData] = useState({});
    const [savingApplicationData, setSavingApplicationData] = useState({});

    const fileTemplateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/FileTemplate";
    const createReportApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/Report/create-report";

    // Initialize insurance application data from formData when component mounts or formData changes
    useEffect(() => {
        if (formData.filesToBeWorked && formData.filesToBeWorked.length > 0) {
            const initialData = {};
            formData.filesToBeWorked.forEach((accidentFileEntry) => {
                const accidentFileId = accidentFileEntry.accidentFileId;
                if (accidentFileId && (accidentFileEntry.insuranceApplicationType || accidentFileEntry.insuranceApplicationDate)) {
                    initialData[accidentFileId] = {
                        insuranceApplicationType: accidentFileEntry.insuranceApplicationType || '',
                        insuranceApplicationDate: accidentFileEntry.insuranceApplicationDate ? 
                            formatDateTimeForInput(accidentFileEntry.insuranceApplicationDate) : ''
                    };
                }
            });
            
            // Only update if there's actual data to set
            if (Object.keys(initialData).length > 0) {
                setInsuranceApplicationData(prev => ({
                    ...prev,
                    ...initialData
                }));
            }
        }
    }, [formData.filesToBeWorked]);

    // Fetch report templates when component mounts
    useEffect(() => {
        const fetchReportTemplates = async () => {
            setLoadingTemplates(true);
            try {
                const response = await fetchFormData({
                    apiEndpoint: fileTemplateApiUrl,
                    page: 1,
                    pageSize: 100,
                    search: null,
                });

                console.log('Report templates API response:', response);

                let templates = [];
                if (response && response.isSuccess && response.data && Array.isArray(response.data.items)) {
                    templates = response.data.items.filter(template => template.isExpertReport === false);
                } else if (response && Array.isArray(response.items)) {
                    templates = response.items.filter(template => template.isExpertReport === false);
                } else if (Array.isArray(response)) {
                    templates = response.filter(template => template.isExpertReport === false);
                }

                setReportTemplates(templates);
                console.log('Sigorta dilekçe şablonları yüklendi:', templates);
            } catch (error) {
                console.error('Error fetching report templates:', error);
                toast.error('Rapor şablonları yüklenirken bir hata oluştu.');
                setReportTemplates([]);
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchReportTemplates();
    }, [fileTemplateApiUrl]);

    // Helper function to check field access based on permissions
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled
        if (!canEdit) {
            return {
                disabled: true,
                required: false,
            };
        }

        // Ensure fieldPermissions is an array before calling .find()
        const safeFieldPermissions = Array.isArray(fieldPermissions) ? fieldPermissions : [];
        // Case-insensitive comparison for robustness
        const permission = safeFieldPermissions.find(p => p.fieldName.toLowerCase() === fieldName.toLowerCase());
        // Return 'disabled' directly for easier use
        return {
            // Set disabled to true if not editable (ReadOnly) or not found (default to not editable for safety)
            disabled: permission ? permission.access !== 'Editable' : true,
            required: permission ? permission.required : false,
        };
    };

    // Helper function to check if file type is LOR (Hak Mahrumiyeti)
    const isLorFileType = (title) => {
        if (!title) return false;
        const lowerTitle = title.toLowerCase();
        return lowerTitle.includes('hak mahrumiyeti') ||
               lowerTitle.includes('lor') ||
               lowerTitle.includes('mahrumiyeti');
    };

    // Format date for input field (YYYY-MM-DDTHH:MM format for datetime-local)
    const formatDateTimeForInput = (dateTimeString) => {
        if (!dateTimeString) return '';
        try {
            const date = new Date(dateTimeString);
            if (isNaN(date.getTime())) return '';
            
            // Format as YYYY-MM-DDTHH:MM
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Handle insurance application data change for specific accident file
    const handleInsuranceApplicationChange = (accidentFileId, field, value) => {
        setInsuranceApplicationData(prev => ({
            ...prev,
            [accidentFileId]: {
                ...prev[accidentFileId],
                [field]: value
            }
        }));
    };

    // Save insurance application data for specific accident file
    const handleSaveInsuranceApplication = async (accidentFileId) => {
        const applicationData = insuranceApplicationData[accidentFileId];
        
        if (!applicationData?.insuranceApplicationDate) {
            toast.error('Lütfen sigorta başvuru tarihini giriniz.');
            return;
        }
        
        if (!applicationData?.insuranceApplicationType) {
            toast.error('Lütfen sigorta başvuru türünü seçiniz.');
            return;
        }

        setSavingApplicationData(prev => ({ ...prev, [accidentFileId]: true }));
        
        try {
            // Convert to ISO string for API
            const dateObject = new Date(applicationData.insuranceApplicationDate);
            const isoString = dateObject.toISOString();
            
            console.log('Saving insurance application data for accident file:', accidentFileId);
            
            // Use the new API method from ApiMethods.js
            await patchAccidentFileInsuranceApplication({
                accidentFileId: accidentFileId,
                insuranceApplicationType: applicationData.insuranceApplicationType,
                insuranceApplicationDate: isoString,
                readyForArbitration: true
            });
            
            toast.success('Sigorta başvuru bilgileri başarıyla kaydedildi.');
            
            // Update formData to reflect the saved insurance application data
            setFormData(prevFormData => {
                const updatedFilesToBeWorked = prevFormData.filesToBeWorked.map(fileEntry => {
                    if (fileEntry.accidentFileId === accidentFileId) {
                        return {
                            ...fileEntry,
                            insuranceApplicationType: applicationData.insuranceApplicationType,
                            insuranceApplicationDate: isoString,
                            readyForArbitration: true
                        };
                    }
                    return fileEntry;
                });
                
                return {
                    ...prevFormData,
                    filesToBeWorked: updatedFilesToBeWorked
                };
            });
            
            // Don't clear the form - keep the data visible
            // setInsuranceApplicationData(prev => ({
            //     ...prev,
            //     [accidentFileId]: { insuranceApplicationDate: '', insuranceApplicationType: '' }
            // }));
            
        } catch (error) {
            console.error('Error saving insurance application:', error);
            // Error message is already handled in the API method
        } finally {
            setSavingApplicationData(prev => ({ ...prev, [accidentFileId]: false }));
        }
    };

    // Get available file numbers from formData.filesToBeWorked
    const getAvailableFileNumbers = () => {
        if (!formData.filesToBeWorked || formData.filesToBeWorked.length === 0) {
            return [];
        }

        return formData.filesToBeWorked.map((accidentFileEntry, index) => {
            const fileNumber = accidentFileEntry.fileNumber || (accidentFileEntry.accidentFileId ? 
                (() => {
                    const accidentFile = formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId);
                    return accidentFile ? accidentFile.fileNumber : accidentFileEntry.accidentFileId;
                })() : 'Yeni'
            );

            // Get file type titles for this entry
            let allFileTypeTitles = [];
            
            // Handle old format (from handleAdd)
            if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
            } else {
                // Handle new format (from API)
                accidentFileEntry.filesToBeWorked?.forEach(file => {
                    if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                        allFileTypeTitles.push(...file._fileTypeTitles);
                    }
                });
            }

            // Check if this file only contains LOR types (cannot create petitions for LOR-only files)
            const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));
            
            // Skip files that only contain LOR types
            if (!hasNonLorTypes && allFileTypeTitles.length > 0) {
                return null;
            }

            return {
                value: accidentFileEntry.accidentFileId || index.toString(), // Use accidentFileId, fallback to index
                label: fileNumber,
                accidentFileId: accidentFileEntry.accidentFileId,
                fileTypeTitles: allFileTypeTitles
            };
        }).filter(item => item !== null); // Filter out null entries (LOR-only files)
    };

    // Define field access for file number selection and petition generation
    const selectFileNumberAccess = getFieldAccess('insurancecompanyreport.generate');
    const generatePetitionAccess = getFieldAccess('insurancecompanyreport.generate');

    // Handler for creating reports
    const handleCreateReport = async () => {
        if (!selectedFileNumber) {
            toast.error('Lütfen bir dosya numarası seçin.');
            return;
        }
        
        if (!selectedTemplate) {
            toast.error('Lütfen bir dilekçe şablonu seçin.');
            return;
        }

        if (!id) {
            toast.error('Çağrı formu ID bulunamadı.');
            return;
        }

        setCreatingReport(true);
        try {
            await createExpertReport({
                apiEndpoint: createReportApiUrl,
                callFormId: id,
                reportTemplateId: selectedTemplate,
                accidentFileId: selectedFileNumber // Send accidentFileId to API
            });
            
            setSelectedTemplate(''); // Reset selection after successful creation
            setSelectedFileNumber(''); // Reset file number selection
        } catch (error) {
            console.error('Error creating petition:', error);
            // Error message is already handled in the API method
        } finally {
            setCreatingReport(false);
        }
    };

    return (
        <>
            <H5>Sigorta Şirketi Dilekçeleri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="fileNumberSelect">Dosya No <span className="text-danger">*</span></Label>
                        <Input
                            id="fileNumberSelect"
                            type="select"
                            value={selectedFileNumber}
                            onChange={(e) => setSelectedFileNumber(e.target.value)}
                            disabled={!canEdit || generatePetitionAccess.disabled}
                        >
                            <option value="">Dosya numarası seçiniz...</option>
                            {getAvailableFileNumbers().map((fileOption) => (
                                <option key={fileOption.value} value={fileOption.value}>
                                    {fileOption.label} ({fileOption.fileTypeTitles.join(', ')})
                                </option>
                            ))}
                        </Input>
                        {getAvailableFileNumbers().length === 0 && (
                            <small className="text-muted">Henüz dilekçe oluşturulabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için dilekçe oluşturulamaz.)</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="templateSelect">Dilekçe Türü <span className="text-danger">*</span></Label>
                        <Input
                            id="templateSelect"
                            type="select"
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={loadingTemplates || !canEdit || generatePetitionAccess.disabled}
                        >
                            <option value="">Dilekçe türü seçiniz...</option>
                            {reportTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </Input>
                        {loadingTemplates && (
                            <small className="text-muted">Dilekçe şablonları yükleniyor...</small>
                        )}
                        {!loadingTemplates && reportTemplates.length === 0 && (
                            <small className="text-muted">Henüz dilekçe şablonu bulunmamaktadır.</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <FormGroup className="mb-0">
                        <Button 
                            color="primary" 
                            onClick={handleCreateReport}
                            disabled={!selectedFileNumber || !selectedTemplate || creatingReport || loadingTemplates || !canEdit || generatePetitionAccess.disabled}
                        >
                            {creatingReport ? 'Oluşturuluyor...' : 'Dilekçe Oluştur'}
                        </Button>
                    </FormGroup>
                </Col>
            </Row>

            {/* Insurance Application Section for Each Accident File */}
            <div style={{ height: "40px" }}></div>
            <H5>Sigorta Başvuru Bilgileri</H5>
            <div style={{ height: "20px" }}></div>
            
            {formData.filesToBeWorked && formData.filesToBeWorked.length > 0 ? (
                (() => {
                    // Filter files to exclude LOR-only files
                    const filteredFiles = formData.filesToBeWorked.filter((accidentFileEntry) => {
                        // Filter out files that only contain LOR types (same logic as getAvailableFileNumbers)
                        let allFileTypeTitles = [];
                        
                        // Handle old format (from handleAdd)
                        if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                            allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                        } else {
                            // Handle new format (from API)
                            accidentFileEntry.filesToBeWorked?.forEach(file => {
                                if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                    allFileTypeTitles.push(...file._fileTypeTitles);
                                }
                            });
                        }

                        // Check if this file has non-LOR types (can create insurance applications for non-LOR files)
                        const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));
                        
                        // Only show files that have non-LOR types
                        return hasNonLorTypes || allFileTypeTitles.length === 0;
                    });

                    // If no files pass the filter, show the no-files message
                    if (filteredFiles.length === 0) {
                        return (
                            <div className="text-muted">
                                <small>Henüz sigorta başvurusu yapılabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için sigorta başvurusu yapılamaz.)</small>
                            </div>
                        );
                    }

                    // Map filtered files
                    return filteredFiles.map((accidentFileEntry, index) => {
                    const accidentFileId = accidentFileEntry.accidentFileId || index.toString();
                    const fileNumber = accidentFileEntry.fileNumber || (accidentFileEntry.accidentFileId ? 
                        (() => {
                            const accidentFile = formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId);
                            return accidentFile ? accidentFile.fileNumber : accidentFileEntry.accidentFileId;
                        })() : 'Yeni'
                    );

                    // Get file type titles for this entry
                    let allFileTypeTitles = [];
                    
                    // Handle old format (from handleAdd)
                    if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                        allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                    } else {
                        // Handle new format (from API)
                        accidentFileEntry.filesToBeWorked?.forEach(file => {
                            if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                allFileTypeTitles.push(...file._fileTypeTitles);
                            }
                        });
                    }

                    const currentApplicationData = insuranceApplicationData[accidentFileId] || { 
                        insuranceApplicationDate: accidentFileEntry.insuranceApplicationDate ? 
                            formatDateTimeForInput(accidentFileEntry.insuranceApplicationDate) : '', 
                        insuranceApplicationType: accidentFileEntry.insuranceApplicationType || '' 
                    };

                    return (
                        <div key={accidentFileId} style={{ marginBottom: '30px', border: 'none', boxShadow: 'none' }}>
                            <h6 className="mb-3">
                                Dosya No: {fileNumber} 
                                {allFileTypeTitles.length > 0 && (
                                    <small className="text-muted"> ({allFileTypeTitles.join(', ')})</small>
                                )}
                                {/* Show arbitration readiness status */}
                                {accidentFileEntry.readyForArbitration ? (
                                    <span className="badge rounded-pill bg-success ms-2">Tahkime Hazır</span>
                                ) : (
                                    <span className="badge rounded-pill bg-warning ms-2">Tahkime Hazır Değil</span>
                                )}
                            </h6>
                            
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for={`insuranceApplicationDate_${accidentFileId}`}>
                                            Sigorta Başvuru Tarihi
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id={`insuranceApplicationDate_${accidentFileId}`}
                                            type="datetime-local"
                                            value={currentApplicationData.insuranceApplicationDate}
                                            onChange={(e) => handleInsuranceApplicationChange(accidentFileId, 'insuranceApplicationDate', e.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </FormGroup>
                                </Col>
                                
                                <Col md={4}>
                                    <FormGroup>
                                        <Label for={`insuranceApplicationType_${accidentFileId}`}>
                                            Sigorta Başvuru Türü
                                            <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            id={`insuranceApplicationType_${accidentFileId}`}
                                            type="select"
                                            value={currentApplicationData.insuranceApplicationType}
                                            onChange={(e) => handleInsuranceApplicationChange(accidentFileId, 'insuranceApplicationType', e.target.value)}
                                            disabled={!canEdit}
                                        >
                                            <option value="">Başvuru türü seçiniz...</option>
                                            <option value="Kep">Kep</option>
                                            <option value="Mail">Mail</option>
                                            <option value="Elden">Elden</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                                
                                <Col md={4} className="d-flex align-items-end">
                                    <FormGroup className="mb-0">
                                        <Button 
                                            color="success" 
                                            onClick={() => handleSaveInsuranceApplication(accidentFileId)}
                                            disabled={
                                                !canEdit || 
                                                !currentApplicationData.insuranceApplicationDate || 
                                                !currentApplicationData.insuranceApplicationType ||
                                                savingApplicationData[accidentFileId]
                                            }
                                        >
                                            {savingApplicationData[accidentFileId] ? 'Kaydediliyor...' : 'Kaydet'}
                                        </Button>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    );
                });
                })()
            ) : (
                <div className="text-muted">
                    <small>Henüz sigorta başvurusu yapılabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için sigorta başvurusu yapılamaz.)</small>
                </div>
            )}
        </>
    );
};

export default InsuranceCompanyReportForm;
